import { useState, useMemo } from 'react';
import { Cpu, Plus, Search } from 'lucide-react';
import AgentListItem from './AgentListItem';

/**
 * Filtra agentes por nome (internalName ou publicName).
 */
function filterAgentsBySearch(agents, query) {
  if (!query.trim()) return agents;
  const q = query.trim().toLowerCase();
  return agents.filter(
    (a) =>
      (a.internalName || '').toLowerCase().includes(q) ||
      (a.publicName || '').toLowerCase().includes(q)
  );
}

/**
 * Sidebar "Meus Agentes": lista, busca e botão criar.
 *
 * @param {Object} props
 * @param {Array} props.agents
 * @param {string | null} props.selectedAgentId
 * @param {(id: string | null) => void} props.onSelectAgent
 * @param {() => void} props.onCreateAgent
 * @param {Record<string, { name: string }>} [props.teamsMap]
 */
export default function AgentSidebar({
  agents,
  selectedAgentId,
  onSelectAgent,
  onCreateAgent,
  teamsMap = {},
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredAgents = useMemo(
    () => filterAgentsBySearch(agents, searchQuery),
    [agents, searchQuery]
  );

  return (
    <div className="w-72 bg-appSurface border-r border-appSurfaceBorder flex flex-col shrink-0">
      <div className="p-6 border-b border-appSurfaceBorder flex items-center justify-between gap-2">
        <h2 className="font-bold text-appText flex items-center gap-2 tracking-tight shrink-0">
          <Cpu size={18} className="text-appPrimary" /> Meus Agentes
        </h2>
        <button
          type="button"
          onClick={onCreateAgent}
          className="text-appPrimary hover:bg-appPrimaryLight p-1.5 rounded-lg border border-transparent hover:border-appPrimary/30 transition-all shrink-0"
          aria-label="Criar agente"
        >
          <Plus size={18} />
        </button>
      </div>

      {agents.length > 0 && (
        <div className="p-3 border-b border-appSurfaceBorder">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-appTextMuted" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nome..."
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-appSurfaceBorder bg-appSurface text-appText text-xs placeholder:text-appTextMuted focus:ring-2 focus:ring-appPrimary outline-none"
              aria-label="Buscar agentes por nome"
            />
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        {filteredAgents.length === 0 && agents.length > 0 ? (
          <p className="text-appTextMuted text-xs p-4 text-center">
            Nenhum agente encontrado para &quot;{searchQuery}&quot;.
          </p>
        ) : filteredAgents.length === 0 ? (
          <p className="text-appTextMuted text-xs p-4 text-center">
            Nenhum agente configurado. Clique em + para criar.
          </p>
        ) : (
          filteredAgents.map((agent) => (
            <AgentListItem
              key={agent.id}
              agent={agent}
              isSelected={selectedAgentId === agent.id}
              onSelect={onSelectAgent}
              teamsMap={teamsMap}
            />
          ))
        )}
      </div>
    </div>
  );
}
